import { AUDIT_ACTIONS, type AuditAction, type AuditAuthority, type AuditOutcome } from "@lms/shared-types";
import type { Tone } from "../components/ui";

/**
 * The trail in words (Starsach's audit page, the owner's direction of
 * 2026-09-21: "actions in audit use their field name, not the human
 * name"). One label per action of the closed vocabulary, typed against it
 * so a new action without a label fails `tsc`; the vocabulary key stays
 * on the cell as its title, and an unknown key — a build reading a newer
 * trail — falls back to the key itself.
 */
export const AUDIT_ACTION_LABEL: Record<AuditAction, string> = {
  "access.refused": "Access refused",
  "role.granted": "Role granted",
  "role.revoked": "Role revoked",
  "user.created": "Person invited",
  "user.invited": "Invitation sent",
  "user.activated": "First sign-in",
  "user.preferences_changed": "Preferences changed",
  "user.profile_changed": "Profile changed",
  "user.disabled": "Person disabled",
  "user.enabled": "Person enabled",
  "user.removal_requested": "Removal requested",
  "user.removed": "Person removed",
  "session.revoked": "Signed out everywhere",
  "export.produced": "Export produced",
  "assignment.created": "Training assigned",
  "assignment.withdrawn": "Training withdrawn",
  "external_completion.claimed": "Training marked done",
  "external_completion.attested": "Training attested",
  "support_request.created": "Help request sent",
  "support_request.handed_off": "Help request passed to the support desk",
  "support_request.listed": "All help requests read",
  "docs_question.asked": "Question asked of the help",
  "hours_entry.credited": "Hours banked from training",
  "hours_entry.corrected": "Hours corrected",
  "email.sent": "Email sent",
  "email.suppressed": "Email address suppressed",
};

export function actionLabel(action: string): string {
  return (AUDIT_ACTION_LABEL as Record<string, string>)[action] ?? action;
}

/** Every action, for a filter: label and key, in the vocabulary's order. */
export const AUDIT_ACTION_OPTIONS: ReadonlyArray<{ value: AuditAction; label: string }> = AUDIT_ACTIONS.map((a) => ({ value: a, label: AUDIT_ACTION_LABEL[a] }));

export const OUTCOME_WORD: Record<AuditOutcome, string> = { applied: "Applied", refused: "Refused", attempted: "Attempted", failed: "Failed" };
export const OUTCOME_TONE: Record<AuditOutcome, Tone> = { applied: "ok", refused: "danger", attempted: "warn", failed: "danger" };

export const AUTHORITY_WORD: Record<AuditAuthority, string> = { not_required: "No approval needed", approved: "Approved", self_approved: "Self-approved", break_glass: "Break glass" };
export const AUTHORITY_TONE: Record<AuditAuthority, Tone> = { not_required: "neutral", approved: "ok", self_approved: "neutral", break_glass: "danger" };

/**
 * Who, in words: the person by name with their email, or — for a script or
 * an operator acting from the cloud account under an assumed role — the
 * session name at the end of the role ARN, qualified, with the whole ARN
 * left for the details. Never an ARN across a table cell.
 */
export function actorLabel(actor: { name?: string; email?: string; subject: string }): { text: string; qualifier?: string; title?: string } {
  if (actor.name) return { text: `${actor.name} (${actor.email ?? actor.subject})` };
  if (actor.email) return { text: actor.email };
  const arn = /^arn:aws:sts::\d+:assumed-role\/([^/]+)\/(.+)$/.exec(actor.subject);
  if (arn) return { text: arn[2]!, qualifier: "cloud operator", title: actor.subject };
  return { text: actor.subject };
}
